import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
} from '@angular/common/http';

import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { HmacServiceService } from '../services/hmac-service.service';
import { AuthService } from '../services/AuthService';

import { k } from 'src/environments/properties';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private refreshing = false;

  constructor(
    private hmacService: HmacServiceService,
    private authService: AuthService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    const secretKey = environment.secretKeyHMAC;


    /*
     * ==========================================
     * GENERAR HMAC
     * ==========================================
     */

    const fullUrl = new URL(
      req.urlWithParams,
      window.location.origin
    );

    const relativeUrl =
      `${fullUrl.host}${fullUrl.pathname}${fullUrl.search}`;

    const hmac = this.hmacService.generateHmac(
      relativeUrl,
      secretKey
    );


    /*
     * ==========================================
     * IDENTIFICAR ENDPOINTS
     * ==========================================
     */

    const isAuthLogin =
      req.url.includes('/auth/login');

    const isAuthRefresh =
      req.url.includes('/auth/refresh');

    const isAuthLogout =
      req.url.includes('/auth/logout');


    /*
     * ==========================================
     * ENDPOINTS MFA
     * ==========================================
     *
     * Estos endpoints utilizan partial_token
     * mientras el proceso de autenticación
     * todavía no ha finalizado.
     */

    const isMfaLogin =
      req.url.includes('/auth/2fa/login');

    const isMfaGenerate =
      req.url.includes('/auth/2fa/qrcode/generate');

    const isMfaValidate =
      req.url.includes('/auth/2fa/validate');


    const isMfaEndpoint =
      isMfaLogin ||
      isMfaGenerate ||
      isMfaValidate;


    /*
     * ==========================================
     * SESIÓN DE INTEGRACIÓN
     * ==========================================
     *
     * Si existe un token en sessionStorage,
     * estamos trabajando con la sesión temporal
     * proveniente del Sistema A.
     */

    const sessionToken =
      sessionStorage.getItem(k.token);

    const isIntegrationSession =
      !!sessionToken;


    /*
     * ==========================================
     * OBTENER TOKEN CORRECTO
     * ==========================================
     *
     * MFA
     *   → partial_token
     *
     * resto de endpoints
     *   → access_token
     */

    const token = this.authService.getToken(
      isMfaEndpoint
        ? 'partial'
        : 'access'
    );


    /*
     * ==========================================
     * CONSTRUIR REQUEST
     * ==========================================
     */

    let authReq = req;


    /*
     * Login tradicional, refresh y logout
     * no reciben Authorization.
     *
     * Los endpoints MFA SÍ reciben Authorization,
     * utilizando partial_token.
     */

    if (
      !isAuthLogin &&
      !isAuthRefresh &&
      !isAuthLogout &&
      token
    ) {

      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          Signature: hmac,
        },
      });

    } else {

      /*
       * Siempre enviamos la firma HMAC.
       */

      authReq = req.clone({
        setHeaders: {
          Signature: hmac,
        },
      });

    }


    /*
     * ==========================================
     * EJECUTAR PETICIÓN
     * ==========================================
     */

    return next.handle(authReq).pipe(

      catchError((error: any) => {

        /*
         * Solo manejamos errores HTTP.
         */

        if (!(error instanceof HttpErrorResponse)) {

          return throwError(
            () => error
          );

        }


        const is401 =
          error.status === 401;


        /*
         * ==========================================
         * ENDPOINTS QUE NO HACEN REFRESH
         * ==========================================
         *
         * MFA utiliza partial_token.
         *
         * Si devuelve 401:
         *
         * - partial_token expiró
         * - código MFA incorrecto
         * - token inválido
         *
         * NO debemos ejecutar refresh().
         */

        if (
          !is401 ||
          isAuthLogin ||
          isAuthRefresh ||
          isAuthLogout ||
          isMfaEndpoint
        ) {

          return throwError(
            () => error
          );

        }


        /*
         * ==========================================
         * SESIÓN DE INTEGRACIÓN
         * ==========================================
         *
         * Si el token proviene de sessionStorage,
         * estamos trabajando con el Sistema A.
         *
         * No se debe intentar refresh porque el
         * refresh token podría pertenecer a una
         * sesión normal almacenada en Sistema B.
         */

        if (isIntegrationSession) {

          this.clearIntegrationSession();

          return throwError(
            () => error
          );

        }


        /*
         * ==========================================
         * SESIÓN NORMAL
         * ==========================================
         */

        /*
         * Si ya existe un refresh en proceso
         * y vuelve otro 401, consideramos
         * que la sesión expiró completamente.
         */

        if (this.refreshing) {

          this.authService.forceLocalLogout(
            true,
            'expired'
          );

          return throwError(
            () => error
          );

        }


        this.refreshing = true;


        /*
         * ==========================================
         * REFRESH TOKEN
         * ==========================================
         */

        return from(
          this.authService.refresh()
        ).pipe(

          switchMap((newToken) => {

            this.refreshing = false;


            /*
             * Refresh no devolvió token.
             */

            if (!newToken) {

              this.authService.forceLocalLogout(
                true,
                'expired'
              );

              return throwError(
                () => error
              );

            }


            /*
             * Volvemos a generar HMAC.
             */

            const newHmac =
              this.hmacService.generateHmac(
                relativeUrl,
                secretKey
              );


            /*
             * Reintentamos la petición original
             * con el nuevo access_token.
             */

            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
                Signature: newHmac,
              },
            });


            return next.handle(
              retryReq
            );

          }),


          /*
           * ==========================================
           * ERROR EN REFRESH
           * ==========================================
           */

          catchError((refreshErr: any) => {

            this.refreshing = false;


            this.authService.forceLocalLogout(
              true,
              'expired'
            );


            return throwError(
              () => refreshErr
            );

          }),

        );

      }),

    );

  }


  /**
   * Limpia exclusivamente la sesión
   * temporal proveniente del Sistema A.
   *
   * NO limpia localStorage porque allí
   * podría existir una sesión normal
   * válida del Sistema B.
   */
  private clearIntegrationSession(): void {

    sessionStorage.removeItem(k.user_id);
    sessionStorage.removeItem(k.token);
    sessionStorage.removeItem(k.user_name);
    sessionStorage.removeItem(k.tipoGrupo);
    sessionStorage.removeItem(k.correo);

  }

}


export const AuthInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};